const RES_OPERATION_SAFARI_NOISE_MESSAGE =
  "'TypeError' captured as exception with message: 'undefined is not an object (evaluating 'res.operation')'"

// A single synthetic Safari/WebKit event used this exact message with no stack
// frames, which identifies injected third-party noise. Keep both boundaries so
// genuine application errors mentioning the property remain visible.
export function isFramelessResOperationNoise({
  msg,
  frames,
}: {
  msg: string
  frames: readonly unknown[]
}): boolean {
  return frames.length === 0 && msg === RES_OPERATION_SAFARI_NOISE_MESSAGE
}

export const STALE_BUILD_RECOVERY_EVENT = "pleasurai:stale-build-recovery"

/**
 * Detects the Next.js error raised when a cached client bundle calls a server
 * action that no longer exists after a deployment.
 */
export function isStaleServerActionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false

  const { name, message } = error as { name?: unknown; message?: unknown }
  if (name === "UnrecognizedActionError") return true
  if (typeof message !== "string") return false

  return (
    message.includes("Failed to find Server Action") ||
    message.includes("was not found on the server")
  )
}

export function isReactHydrationMismatchFromGeneratedChunk({
  msg,
  sources,
  functions,
}: {
  msg: string
  sources: unknown
  functions: unknown
}): boolean {
  const normalized = msg.trim()
  if (!normalized.startsWith("Minified React error #418;")) return false
  if (!normalized.includes("react.dev/errors/418?args[]=")) return false
  if (
    !normalized.includes("args[]=HTML") &&
    !normalized.includes("args[]=text")
  ) {
    return false
  }
  if (!Array.isArray(functions) || functions.length !== 0) return false
  if (!Array.isArray(sources) || sources.length !== 1) return false

  return isGeneratedTopLevelChunk(valueToString(sources[0]).trim())
}

// Local development sessions that run against the production PostHog key forward
// their crashes into production error tracking. These are almost always local
// prototypes and next-start noise (e.g. a Babylon.js companion prototype on
// `http://127.0.0.1:3011`), never real user failures. Drop any client exception
// captured from a loopback/localhost/mDNS host so it stays out of the production
// error inbox. Deployed environments (production and Coolify previews) serve on
// real hostnames, so their exceptions are unaffected.
export function isLocalDevExceptionHost(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/^\[|\]$/g, "")
  if (!host) return false
  if (host === "localhost" || host.endsWith(".localhost")) return true
  if (host === "::1" || host === "0.0.0.0") return true
  if (host.endsWith(".local")) return true
  // IPv4 loopback range 127.0.0.0/8
  if (/^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return true
  return false
}

export function isSuppressedStorageAccessSecurityError({
  type,
  msg,
}: {
  type: string
  msg: string
}): boolean {
  const isSecurityError =
    type === "SecurityError" ||
    (type === "DOMException" && msg.startsWith("SecurityError"))

  if (!isSecurityError) return false

  // Firefox uses this message when privacy settings block browser storage. It
  // is the same benign environment failure as the existing localStorage case.
  return msg.includes("localStorage") || msg === "The operation is insecure."
}

// Trackdesk reports this exact synthetic, unhandled error when its queued
// `click` command runs for a visitor without affiliate URL parameters. It is
// vendor noise: the attribution flow is only meaningful when those parameters
// exist, and the SDK remains responsible for handling its own no-op case.
export function isTrackdeskParamsNotSetException({
  msg,
  type,
}: {
  msg: string
  type: string
}): boolean {
  return (
    type === "Error" &&
    msg === "'Error' captured as exception with message: 'Params are not set'"
  )
}

export function isFramelessGmeWalletProviderError({
  msg,
  frames,
  mechanism,
}: {
  msg: string
  frames: unknown
  mechanism: unknown
}): boolean {
  // GME is the GameStop browser-wallet extension. Its exact synthetic
  // window.onerror event has no stack frames. Require all three signatures so
  // a similarly worded application exception stays visible.
  return (
    Array.isArray(frames) &&
    frames.length === 0 &&
    msg.trim() ===
      "'Disconnected' captured as exception with message: 'GME Provider is disconnected or locked'" &&
    isSyntheticExceptionMechanism(mechanism)
  )
}

function isSyntheticExceptionMechanism(mechanism: unknown): boolean {
  return (
    typeof mechanism === "object" &&
    mechanism !== null &&
    !Array.isArray(mechanism) &&
    (mechanism as Record<string, unknown>).synthetic === true
  )
}

/**
 * Browser stream readers surface an interrupted response as this handled
 * TypeError. The stream callers already present a retryable user-facing state,
 * so sending it to error tracking only creates noise.
 */
export function isExpectedStreamInterruption({
  msg,
  scope,
  type,
}: {
  msg: string
  scope: string
  type: string
}): boolean {
  return scope === "client-stream" && type === "TypeError" && msg === "Error in input stream"
}

function valueToString(value: unknown): string {
  return typeof value === "string" ? value : ""
}

function isGeneratedTopLevelChunk(source: string): boolean {
  const match = source.match(/(?:^|https?:\/\/[^/]+)\/_next\/static\/chunks\/([^/]+)$/)
  if (!match) return false

  // The observed #418 noise was reported from one obfuscated Turbopack chunk
  // such as `10q3vw~ufna~s.js` or `0i0escodlo3.2.js`, with no function names.
  // Do not suppress app route chunks (`chunks/app/...`) or named framework
  // chunks because those can carry genuine app hydration failures.
  return /^[0-9][a-z0-9~.-]*\.js$/i.test(match[1])
}
